/**
 * Billing hooks for React Query.
 *
 * Provides data fetching and mutations for:
 *   - Plan catalog (pricing page)
 *   - Billing status (billing dashboard)
 *   - Checkout session creation
 *   - Customer portal session
 *   - Plan upgrades
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { apiClient } from '../api/client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Plan {
  id: string;
  tier: string;
  displayName: string;
  monthlyPriceCents: number;
  annualPriceCents: number | null;
  currency: string;
  monthlyCredits: number;
  defaultModel: string;
  /** Human-friendly tier name (e.g. "Advanced AI") — never shows raw model IDs. */
  modelTierDisplay: string;
  maxAgents: number;
  features: Record<string, unknown> | null;
  sortOrder: number;
}

export interface BillingStatus {
  subscription: {
    id: string;
    status: string;
    billingInterval: string;
    currentPeriodEnd: string;
    cancelAtPeriodEnd: boolean;
    plan: Plan;
  } | null;
  pendingDowngrade: {
    planName: string;
    effectiveDate: string;
    interval: 'month' | 'year';
  } | null;
  credits: {
    balance: number;
    monthlyAllowance: number;
    usedThisPeriod: number;
  };
  usage: {
    totalCredits: number;
    totalCostUsd: number;
    recordCount: number;
    byModel: Record<string, { credits: number; count: number }>;
    byCategory: Record<string, { credits: number; count: number }>;
    categoryLabels: Record<string, string>;
  };
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function usePlans() {
  return useQuery({
    queryKey: ['billing', 'plans'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ plans: Plan[] }>('/billing/plans');
      return data.plans;
    },
    staleTime: 5 * 60 * 1000, // Plans rarely change, cache for 5 min
  });
}

export function useBillingStatus(enabled = true) {
  return useQuery({
    queryKey: ['billing', 'status'],
    queryFn: async () => {
      const { data } = await apiClient.get<BillingStatus>('/billing/status');
      return data;
    },
    staleTime: 30 * 1000, // Refresh every 30s for live credit tracking
    enabled,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreateCheckout() {
  return useMutation({
    mutationFn: async ({
      planId,
      interval,
    }: {
      planId: string;
      interval: 'month' | 'year';
    }) => {
      const { data } = await apiClient.post<{ url: string }>('/billing/checkout', {
        planId,
        interval,
      });
      return data.url;
    },
    onSuccess: (url) => {
      // Redirect to Stripe Checkout
      window.location.href = url;
    },
  });
}

export function useCreatePortalSession() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<{ url: string }>('/billing/portal');
      return data.url;
    },
    onSuccess: (url) => {
      // Redirect to Stripe Customer Portal
      window.location.href = url;
    },
  });
}

export interface PlanChangeResult {
  success: boolean;
  scheduled: boolean;
  message: string;
  effectiveDate?: string | null;
  newPlan?: string;
}

export function useUpgradePlan() {
  return useMutation({
    mutationFn: async ({ planId, interval }: { planId: string; interval?: 'month' | 'year' }) => {
      const { data } = await apiClient.post<PlanChangeResult>(
        '/billing/upgrade',
        { planId, interval },
      );
      return data;
    },
  });
}

export function useCancelDowngrade() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<{ success: boolean; message: string }>(
        '/billing/cancel-downgrade',
      );
      return data;
    },
  });
}
